/**
 * <document-redirect>
 *
 * Sends a QR-code visitor straight to the PDF that matches their browser
 * language.
 *
 * The server renders every available language and picks none — Shopify caches
 * storefront HTML at the CDN per URL, not per Accept-Language header, so a
 * server-side choice would be frozen into the cache for every later visitor.
 * This element does the picking, client-side, per visitor. For the same reason
 * every visible sentence ships in the JSON payload and is swapped here: Liquid's
 * `t` output is only ever the no-JS fallback.
 *
 * Three escape hatches keep a wrong guess from trapping the visitor in an
 * in-app browser's PDF viewer, where there is no back button:
 *   1. `location.href` (not `replace`) plus a sessionStorage guard, so pressing
 *      back returns to this page and it does not immediately redirect again.
 *   2. `?stay=1` forces the landing view — for support links and QA.
 *   3. Without JavaScript nothing redirects and Liquid's full language list is
 *      the page, revealed through base.css's `.no-js-inline` / `.no-js-hidden`.
 */
if (!customElements.get('document-redirect')) {
  /**
   * Deprecated ISO 639 codes still emitted by some browsers. Kept outside the
   * class body: static class fields are ES2022 and unsupported by the older
   * WebViews that QR scanners on aging phones still ship.
   */
  const LEGACY_LANGUAGE_CODES = { iw: 'he', in: 'id', ji: 'yi', no: 'nb', nn: 'nb' };

  /** Own-property lookup, so `?lang=constructor` cannot reach Object.prototype. */
  const mapLegacy = (code) =>
    Object.prototype.hasOwnProperty.call(LEGACY_LANGUAGE_CODES, code) ? LEGACY_LANGUAGE_CODES[code] : code;

  customElements.define(
    'document-redirect',
    class DocumentRedirect extends HTMLElement {
      connectedCallback() {
        const source = document.getElementById(this.dataset.json);
        if (!source) return;

        let data;
        try {
          data = JSON.parse(source.textContent);
        } catch (error) {
          return; // Keep the server-rendered fallback.
        }

        this.files = Array.isArray(data.files) ? data.files : [];
        this.strings = data.strings || {};
        this.fallback = data.fallback || 'en';
        this.byCode = new Map(this.files.map((file) => [file.code, file]));

        if (this.files.length === 0) {
          // No file in any language. Still translate the page — the visitor has
          // to be able to read why nothing is on offer.
          this.translate(this.pick(Object.keys(this.strings)).code);
          this.track(null, 'document_missing');
          return;
        }

        const choice = this.pick(this.files.map((file) => file.code));
        const resolved = this.byCode.get(choice.code);

        this.apply(resolved);
        this.translate(resolved ? resolved.code : this.fallback);

        // Nothing the visitor asked for was on offer — say so, rather than
        // silently handing them a language they did not choose.
        if (!choice.matched) this.reveal('[data-document-hint]');

        this.addEventListener('click', this.onClick.bind(this));

        this.redirect(resolved);
      }

      /**
       * Candidate language tags, highest precedence first.
       * `?stay=1` and the back-navigation guard are handled in `redirect()`:
       * they suppress the jump but must not change which language is shown.
       */
      preferenceOrder() {
        const order = [];
        const override = new URLSearchParams(window.location.search).get('lang');
        if (override) order.push(override);

        // Only trust the storefront locale when the URL actually carries a
        // locale prefix. On the neutral QR URL it is merely the shop's primary
        // language and must not outrank the visitor's browser preference.
        if (this.dataset.localeExplicit === 'true' && this.dataset.locale) {
          order.push(this.dataset.locale);
        }

        // `navigator.languages` is missing on older Android WebViews.
        const tags =
          navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
        tags.forEach((tag) => {
          if (tag) order.push(tag);
        });

        return order;
      }

      /**
       * Best available code out of `codes`, following the preference order.
       * `matched` is false when nothing the visitor asked for was on offer and
       * the fallback had to step in.
       *
       * @returns {{code: string|null, matched: boolean}}
       */
      pick(codes) {
        if (!codes || codes.length === 0) return { code: null, matched: false };

        for (const tag of this.preferenceOrder()) {
          const hit = DocumentRedirect.matchIn(codes, tag);
          if (hit) return { code: hit, matched: true };
        }
        return {
          code: codes.indexOf(this.fallback) > -1 ? this.fallback : codes[0],
          matched: false,
        };
      }

      static matchIn(codes, tag) {
        const candidates = DocumentRedirect.normalize(tag);
        for (const candidate of candidates) {
          if (codes.indexOf(candidate) > -1) return candidate;
        }

        // Reverse-region match: the browser says 'pt', we only ship 'pt-br'.
        const base = candidates[candidates.length - 1];
        return codes.filter((code) => code.split('-')[0] === base)[0] || null;
      }

      /** 'de-AT' -> ['de-at', 'de'];  'zh-Hans-CN' -> ['zh-cn', 'zh'] */
      static normalize(tag) {
        let value = String(tag).toLowerCase().replace(/_/g, '-');
        if (value.indexOf('zh-hans') === 0) value = 'zh-cn';
        if (value.indexOf('zh-hant') === 0) value = 'zh-tw';

        // Map the full tag and the base separately, so 'iw-IL' normalises the
        // same way 'iw' does.
        value = mapLegacy(value);
        const base = mapLegacy(value.split('-')[0]);

        // Drop empties: a tag like '__proto__' normalises to an empty base,
        // which must never be offered as a matchable code.
        return [value, base].filter((code, index, all) => code && all.indexOf(code) === index);
      }

      /** Point the primary button at the resolved file. */
      apply(file) {
        const link = this.querySelector('[data-document-primary]');
        if (!link || !file) return;

        link.href = file.url;
        link.setAttribute('hreflang', file.code);

        const label = this.querySelector('[data-document-primary-language]');
        if (label) label.textContent = file.name;

        this.dataset.resolved = file.code;

        const listed = this.querySelector(`[data-document-language="${file.code}"]`);
        if (listed) listed.setAttribute('aria-current', 'true');
      }

      reveal(selector) {
        const node = this.querySelector(selector);
        if (node) node.hidden = false;
      }

      /**
       * Swap every visible sentence. These cannot come from Liquid's `t` filter,
       * which resolves against the URL's locale — and the cached HTML has
       * exactly one of those for all visitors.
       */
      translate(code) {
        const dictionary = code && this.strings[code.split('-')[0]];
        if (!dictionary) return;

        this.querySelectorAll('[data-document-i18n]').forEach((node) => {
          const value = dictionary[node.dataset.documentI18n];
          if (value) node.textContent = value;
        });

        // Accessible names carry the same problem as visible text.
        this.querySelectorAll('[data-document-i18n-label]').forEach((node) => {
          const value = dictionary[node.dataset.documentI18nLabel];
          if (value) node.setAttribute('aria-label', value);
        });

        const kindNode = this.querySelector('[data-document-kind]');
        if (kindNode && dictionary.kind) {
          const label = dictionary.kind[kindNode.dataset.documentKind];
          if (label) kindNode.textContent = label;
        }
      }

      /** @returns {boolean} whether the visitor was sent to the PDF. */
      redirect(file) {
        if (!file) {
          this.track(null, 'document_view');
          return false;
        }

        // Never navigate inside the theme customizer — it would hijack the
        // preview iframe and make this section impossible to configure.
        if (window.Shopify && window.Shopify.designMode) {
          this.track(file, 'document_view');
          return false;
        }

        const params = new URLSearchParams(window.location.search);
        if (params.has('stay')) {
          this.track(file, 'document_view');
          return false;
        }

        // Keyed by language, not just by document: a visitor who pressed back
        // is not sent to the same PDF again, while an explicit ?lang= link still
        // works in a tab that already redirected once.
        const key = `doc:${this.dataset.handle}:${file.code}`;
        if (DocumentRedirect.readGuard(key)) {
          this.track(file, 'document_view');
          return false;
        }

        DocumentRedirect.writeGuard(key, '1');

        const status = this.querySelector('[data-document-status]');
        if (status) status.hidden = false;

        // Push before navigating: once `location.href` is assigned the document
        // can be torn down before GTM has flushed the event.
        this.track(file, 'document_redirect');
        window.location.href = file.url;
        return true;
      }

      /** sessionStorage throws in private-mode WebViews; degrade to no guard. */
      static readGuard(key) {
        try {
          return Boolean(window.sessionStorage.getItem(key));
        } catch (error) {
          return false;
        }
      }

      static writeGuard(key, value) {
        try {
          window.sessionStorage.setItem(key, value);
        } catch (error) {
          // Ignore: the visitor simply loses the back-button guard.
        }
      }

      track(file, event) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: event,
          document_handle: this.dataset.handle,
          document_language: file ? file.code : null,
          browser_language: (navigator.languages || [])[0] || navigator.language || null,
        });
      }

      onClick(event) {
        const link = event.target.closest('a[hreflang]');
        if (!link || !this.contains(link)) return;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'document_download',
          document_handle: this.dataset.handle,
          document_language: link.getAttribute('hreflang'),
          document_source: link.hasAttribute('data-document-primary') ? 'primary' : 'list',
        });
      }
    }
  );
}
